// Moving a line to another customer's account.
//
// Kevin, 2026-08-26, from support ticket #23: "John King has 2 lines under his account and one is for
// John 'JJ' King... Not sure of the correct process to move a line over to the correct account."
// There was no process, so the answer to the ticket was "you can't" until this existed.
//
// WHY IT IS A PREVIEW AND NOT A DROPDOWN. Ownership moving is one line of the plan. The rest of it is
// severances — the previous customer's caller-ID name comes off the number, their voicemail comes off
// the mailbox, their ring groups are cut — and none of that is guessable from a button labelled
// "Move". So the plan is fetched, read, and only then executed. Same shape as the port-completion
// flow: preview, then a commit that is explicit about being one.
//
// THE TWO THINGS IT DOES NOT DO get their own panel: 911 stays exactly where it was, and the carrier
// writes wait for Save and Apply. Both are deliberate, and both would be dangerous as silent
// defaults, so they are printed rather than implied.

import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { nameTwins, subscriberIdentity, subscriberLabel } from '../../lib/subscriberIdentity.js';

export default function MoveLineModal({ line, onClose, onDone }) {
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState(null);
  const [agreed, setAgreed] = useState(false);

  const subsQ = useQuery({
    queryKey: ['subscribers', 'for-move'],
    queryFn: () => api.get('/subscribers'),
  });
  const all = Array.isArray(subsQ.data) ? subsQ.data : (subsQ.data?.subscribers || []);
  const candidates = all
    .filter((s) => Number(s.id) !== Number(line.subscriberId))
    .filter((s) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      // SEARCHABLE BY THE THING YOU HAVE. An operator working a ticket has a number or a street, not
      // a spelling — and with two John Kings the name is the one term that cannot narrow it down.
      const hay = [
        s.name, s.displayName, s.email, s.extension, s.phone,
        ...(s.numbers || []), ...(s.addresses || []),
      ].filter(Boolean).join(' ').toLowerCase();
      if (hay.includes(q)) return true;
      // DIGITS COMPARED AS DIGITS. A number gets typed "(303) 993-8416" and stored "+13039938416";
      // any comparison that keeps the punctuation — or the spaces — matches neither against the other.
      const digits = q.replace(/\D/g, '');
      return digits.length >= 3 && hay.replace(/\D/g, '').includes(digits);
    })
    .slice(0, 8);

  const planQ = useQuery({
    queryKey: ['line-move-plan', line.id, target?.id],
    queryFn: () => api.get(`/lines/${line.id}/move/plan`, { to_subscriber: target.id }),
    enabled: Boolean(target),
    retry: false,
  });
  const plan = planQ.data;
  const blocked = (plan?.blockers?.length ?? 0) > 0;

  // SAME NAME, DIFFERENT CUSTOMER. Kevin, 2026-08-26: "this highlights an issue when two subscribers
  // have the same name, which rare, can happen." It already had — this modal exists because a line
  // spent a month on John King's account when it was John "JJ" King's. A picker that shows only names
  // is how that happens again, on a control whose whole job is reassigning a phone number.
  const twins = target ? nameTwins(target, all) : [];
  const ambiguous = twins.length > 0;

  const move = useMutation({
    // `dry_run: false` is the whole difference between this and the plan above, and it is spelled out
    // rather than defaulted — the server defaults to a preview precisely so a mistake here is inert.
    mutationFn: () => api.post(`/lines/${line.id}/move`, { to_subscriber: target.id, dry_run: false },
      undefined, { timeoutMs: 60_000 }),
    onSuccess: (res) => onDone?.(res),
  });

  const done = move.data?.ok;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Move this line to another account</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {line.number || `Line ${line.id}`}
              {line.extension ? ` · extension ${line.extension}` : ''}
              {line.subscriberName ? ` · currently ${line.subscriberName}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        {done ? (
          <div className="space-y-3 px-5 py-4">
            <div className="rounded-md border border-green-200 bg-green-50 p-3">
              <p className="text-sm font-medium text-green-900">
                {move.data.line.number || `Line ${move.data.line.id}`} is now on {move.data.to.name}&rsquo;s account.
              </p>
              <p className="mt-1 text-xs text-green-800">{move.data.build_note}</p>
            </div>
            {move.data.warnings?.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                {/* Carried out rather than swallowed: a greeting that would not delete is the previous
                    customer's voice still on a mailbox somebody else now answers. */}
                <p className="text-xs font-medium text-amber-900">Worth a look:</p>
                <ul className="mt-1 list-disc pl-4 text-xs text-amber-800">
                  {move.data.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button type="button" onClick={onClose}
                      className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700">
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 px-5 py-4">
            {/* ---- Who ---- */}
            <div>
              <label htmlFor="move-target" className="block text-xs font-medium text-gray-700">
                Which account should it move to?
              </label>
              <input
                id="move-target"
                value={target ? subscriberLabel(target) : query}
                onChange={(e) => { setTarget(null); setAgreed(false); setQuery(e.target.value); }}
                placeholder="Start typing a customer name"
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
              />
              {!target && (
                <div className="mt-1 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                  {subsQ.isLoading && <p className="px-2 py-1.5 text-xs text-gray-400">Loading…</p>}
                  {!subsQ.isLoading && candidates.length === 0 && (
                    <p className="px-2 py-1.5 text-xs text-gray-400">No other accounts match.</p>
                  )}
                  {candidates.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => { setTarget(s); setAgreed(false); }}
                      className="flex w-full flex-col items-start gap-0.5 px-2 py-1.5 text-left hover:bg-blue-50"
                    >
                      <span className="truncate text-sm text-gray-800">
                        {s.name || s.displayName || `Account ${s.id}`}
                        {/* Flagged in the LIST, not only after picking — the moment to notice there
                            are two of them is before choosing one. */}
                        {nameTwins(s, all).length > 0 && (
                          <span className="ml-1 rounded bg-amber-100 px-1 text-[11px] font-medium text-amber-800">
                            same name as another account
                          </span>
                        )}
                      </span>
                      {/* The number and the street: what an operator holds off a ticket or a call. */}
                      <span className="truncate text-xs text-gray-500">{subscriberIdentity(s)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ---- What it would do ---- */}
            {planQ.isLoading && <p className="text-sm text-gray-500">Working out what that would do…</p>}
            {planQ.error && (
              <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {planQ.error.body?.error || planQ.error.message}
              </p>
            )}

            {/* NAMED SIDE BY SIDE, with the facts that differ. "Are you sure?" is useless here; what an
                operator needs is to see both rows at once and recognise which one is their customer. */}
            {ambiguous && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                  There {twins.length > 1 ? 'are' : 'is'} {twins.length + 1} account{twins.length ? 's' : ''} with this name
                </p>
                <ul className="mt-1 space-y-1 text-xs text-amber-900">
                  <li>
                    <span className="font-medium">Moving to:</span> {target.name || target.displayName}
                    {' — '}{subscriberIdentity(target)}
                  </li>
                  {twins.map((t) => (
                    <li key={t.id} className="text-amber-800">
                      <span className="font-medium">Not:</span> {t.name || t.displayName}
                      {' — '}{subscriberIdentity(t)}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-[11px] text-amber-800">
                  This is how the line ended up on the wrong account in the first place. Check the
                  number and the address against the customer, not the name.
                </p>
              </div>
            )}

            {plan && blocked && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <p className="text-sm font-medium text-red-900">This line cannot move yet.</p>
                <ul className="mt-1 space-y-1 text-xs text-red-800">
                  {plan.blockers.map((b) => <li key={b.code}>• {b.message}</li>)}
                </ul>
              </div>
            )}

            {plan && !blocked && (
              <>
                <div className="rounded-md border border-gray-200">
                  <p className="border-b border-gray-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    What this does
                  </p>
                  <ul className="divide-y divide-gray-100">
                    {plan.steps.map((s) => (
                      <li key={s.step} className="px-3 py-1.5 text-xs text-gray-700">
                        {s.detail}
                        {/* The build is in the list because it is part of the job, and marked because
                            this button is not the thing that does it. */}
                        {s.manual && <span className="ml-1 text-gray-400">(not done by this)</span>}
                      </li>
                    ))}
                  </ul>
                </div>

                {plan.confirm?.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                      Read this before you move it
                    </p>
                    <ul className="mt-1 space-y-1 text-xs text-amber-900">
                      {plan.confirm.map((c) => <li key={c}>• {c}</li>)}
                    </ul>
                    <label className="mt-2 flex items-start gap-2 text-xs text-amber-900">
                      {/* A CHECKBOX, because 911 is on this list. Everything else here is recoverable
                          by moving the line back; sending responders to the wrong address is not. */}
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span>
                        I have checked the 911 address and the points above
                        {ambiguous && `, and that this is the right ${target.name || target.displayName}`}.
                      </span>
                    </label>
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  {plan.from?.name}&rsquo;s voicemail, saved recordings, text conversations and the people
                  on their account all stay with them. A move changes who owns the number, not their history.
                </p>
              </>
            )}

            {move.error && (
              <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {move.error.body?.error || move.error.message}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-3">
              <button type="button" onClick={onClose} className="text-sm text-gray-600 hover:underline">
                Cancel
              </button>
              <button
                type="button"
                disabled={!plan || blocked || !agreed || move.isPending}
                onClick={() => move.mutate()}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {move.isPending
                  ? 'Moving…'
                  : target
                    /* The identity is in the button too: with two John Kings, "Move it to John King" is
                       the last thing somebody reads before committing and it must not be ambiguous. */
                    ? `Move it to ${ambiguous ? subscriberLabel(target) : (target.name || target.displayName || 'that account')}`
                    : 'Move it'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
